import { useEffect, useState } from "react";
import api, { formatIDR, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Paperclip } from "lucide-react";
import { toast } from "sonner";
import AttachmentBox from "@/components/AttachmentBox";

export default function Assets() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = { name: "", category: "", serial_no: "", acquisition_date: "", value: 0, depreciation_years: 0, location: "", status: "active", notes: "" };
  const [form, setForm] = useState(empty);
  const load = () => api.get("/assets").then(r=>setItems(r.data));
  useEffect(()=>{load()},[]);
  const save = async () => {
    try {
      const body = {...form, value:+form.value, depreciation_years:+(form.depreciation_years||0)};
      if (editing) await api.put(`/assets/${editing.id}`, body); else await api.post("/assets", body);
      toast.success("Tersimpan"); setOpen(false); setEditing(null); setForm(empty); load();
    } catch { toast.error("Gagal menyimpan"); }
  };
  const remove = async (id) => { if(!confirm("Hapus?")) return; await api.delete(`/assets/${id}`); load(); };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-tag">Master Data</div>
          <h1 className="font-display text-4xl tracking-tight mt-1">Aset</h1>
          <p className="text-sm text-forest-400 mt-2">Kelola aset tetap bisnis Anda beserta nilai dan lokasinya.</p>
        </div>
        <Dialog open={open} onOpenChange={(v)=>{setOpen(v); if(!v){setEditing(null); setForm(empty);}}}>
          <DialogTrigger asChild>
            <Button data-testid="btn-add-asset" className="rounded-sm bg-[#2D4739] hover:bg-[#22362B] gap-2"><Plus size={16}/> Tambah Aset</Button>
          </DialogTrigger>
          <DialogContent className="bg-[#FDFDFB] rounded-sm max-w-2xl">
            <DialogHeader><DialogTitle className="font-display">{editing?"Ubah":"Tambah"} Aset</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Nama Aset</Label><Input data-testid="inp-asset-name" value={form.name} onChange={e=>setForm({...form, name:e.target.value})}/></div>
              <div><Label>Kategori</Label><Input value={form.category||""} onChange={e=>setForm({...form, category:e.target.value})}/></div>
              <div><Label>Serial No.</Label><Input value={form.serial_no||""} onChange={e=>setForm({...form, serial_no:e.target.value})}/></div>
              <div><Label>Tanggal Perolehan</Label><Input type="date" value={form.acquisition_date||""} onChange={e=>setForm({...form, acquisition_date:e.target.value})}/></div>
              <div><Label>Nilai (IDR)</Label><Input type="number" value={form.value} onChange={e=>setForm({...form, value:e.target.value})}/></div>
              <div><Label>Umur Ekonomis (tahun)</Label><Input type="number" value={form.depreciation_years||0} onChange={e=>setForm({...form, depreciation_years:e.target.value})}/></div>
              <div><Label>Lokasi</Label><Input value={form.location||""} onChange={e=>setForm({...form, location:e.target.value})}/></div>
              <div className="col-span-2"><Label>Catatan</Label><Textarea value={form.notes||""} onChange={e=>setForm({...form, notes:e.target.value})}/></div>
              {editing && (
                <div className="col-span-2 mt-2 border-t border-forest-100 pt-4">
                  <AttachmentBox
                    entityType="assets"
                    entityId={editing.id}
                    attachments={form.attachments || []}
                    onChange={(list) => { setForm({ ...form, attachments: list }); load(); }}
                  />
                </div>
              )}
            </div>
            <DialogFooter><Button data-testid="btn-save-asset" onClick={save} className="rounded-sm bg-[#2D4739] hover:bg-[#22362B]">Simpan</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="card-grid overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-forest-100">
            <th className="text-left px-4 py-3 label-tag">Nama</th>
            <th className="text-left px-4 py-3 label-tag">Kategori</th>
            <th className="text-left px-4 py-3 label-tag">Perolehan</th>
            <th className="text-right px-4 py-3 label-tag">Nilai</th>
            <th className="text-left px-4 py-3 label-tag">Lokasi</th>
            <th className="text-right px-4 py-3 label-tag">Aksi</th>
          </tr></thead>
          <tbody>
            {items.length===0 && <tr><td colSpan={6} className="text-center py-12 text-forest-400">Belum ada aset</td></tr>}
            {items.map(a => (
              <tr key={a.id} className="data-row border-b border-forest-100">
                <td className="px-4 py-3 font-medium">{a.name}</td>
                <td className="px-4 py-3 text-forest-400">{a.category||"-"}</td>
                <td className="px-4 py-3 text-forest-400">{formatDate(a.acquisition_date)}</td>
                <td className="px-4 py-3 text-right num">{formatIDR(a.value)}</td>
                <td className="px-4 py-3 text-forest-400">{a.location||"-"}</td>
                <td className="px-4 py-3 text-right">
                  {a.attachments?.length > 0 && (
                    <span className="inline-flex items-center gap-1 text-xs text-forest-400 mr-2" data-testid={`attach-count-${a.id}`}><Paperclip size={12}/> {a.attachments.length}</span>
                  )}
                  <button onClick={()=>{setEditing(a); setForm({...empty, ...a}); setOpen(true);}} className="p-1.5 hover:bg-forest-50"><Pencil size={14}/></button>
                  <button onClick={()=>remove(a.id)} className="p-1.5 hover:bg-forest-50 text-[#C45A3D]"><Trash2 size={14}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
